<template>
  <div
    v-if="relatedCardIds.length"
    class="related-cards-container"
    :class="{
      'with-suggested-actions': globalState.showSuggestedActions,
    }"
  >
    <!-- toggle header -->
    <div
      class="related-cards-header"
      @click="isExpanded = !isExpanded"
    >
      <div class="related-cards-header-text">
        {{ relatedCards.length }} {{ translate('relatedCards').toLowerCase() }}
      </div>
      <div>
        <em
          class="custom-icons flex h-4 w-4 ml-1"
          :style="getCustomIconStyle(iconPath(isExpanded ? 'collapse' : 'expand'))"
        />
      </div>
    </div>
    <!-- list of cards -->
    <div>
      <vue-scroll
        v-if="isExpanded"
        class="cards-layout--list max-h-64 flex flex-col"
        :ops="{
          scrollPanel: { scrollingX: false },
          bar: { size: '6px', keepShow: true },
          rail: { gutterOfSide: '4px' },
        }"
      >
        <div class="suggested-related-cards view-results">
          <div
            ref="viewResultsCards"
            class="view-results-cards"
          >
            <div
              v-for="(card, index) in relatedCards"
              :key="card.id"
              class="view-card-wrap"
              :data-card-id="(card || {}).id ?? ''"
            >
              <BoardListCard
                :card-id="(card || {}).id ?? ''"
                :index="index"
                :list-cards="relatedCards"
                layout="list"
                :drag-enabled="false"
                :force-hide-drag-handle="true"
                @cardMouseOver="() => {}"
              />
            </div>
          </div>
        </div>
      </vue-scroll>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useStore } from 'vuex';
import { computed, ref } from 'vue';
import BoardListCard from '@/components/widgets/BoardListCard';
import { globalState } from '@/utilities/globalState';
import useCustomIcon from '@/utilities/composables/useCustomIcon';
import useTranslate from '@/utilities/composables/useTranslate';
import useNotes from '@/utilities/composables/useNotes';
const store = useStore();
const { iconPath, getCustomIconStyle } = useCustomIcon();
const isExpanded = ref(true);
const { translate } = useTranslate();
const { getAiNote } = useNotes();

const SIMILARITY_THRESHOLD = 0.5;

const props = defineProps({
  aiNoteId: {
    type: String,
    required: false,
    default: '',
  },
  noteId: {
    type: String,
    required: false,
    default: '',
  },
  templateId: {
    type: String,
    required: false,
    default: '',
  },
});

const aiNote = computed(() => getAiNote(props.noteId, props.templateId));

const relatedCardIds = computed(() => {
  return (
    aiNote.value?.related_resources
      ?.map((resource) => {
        if (resource.type !== 'card' || resource.similarity < SIMILARITY_THRESHOLD) return null;
        return resource.id;
      })
      .filter(Boolean) ?? []
  );
});

const relatedCards = computed(() => {
  if (!relatedCardIds.value.length) return [];
  return relatedCardIds.value.map((id) => {
    const card = store.getters.getCardById(id);
    if (Object.keys(card).length === 0) {
      store.dispatch('fetchCard', { cardId: id }).then((a) => {
        store.dispatch('fetchBoard', { boardId: a.board_id });
      });
    }
    return store.getters.getCardById(id);
  });
});
</script>

<style lang="scss">
.related-cards-container {
  // to hide left side resizer gap
  $resizer-gap: 0.125rem;
  margin-left: -$resizer-gap;

  &.with-suggested-actions {
    width: calc(100% - (439px - $resizer-gap));
  }

  .related-cards-header {
    @apply py-2 px-3 flex items-center cursor-pointer border-t border-solid border-standard-border border-x-0;

    .related-cards-header-text {
      @apply text-sm font-medium text-text-default;
    }
  }

  .suggested-related-cards {
    .view-results-cards {
      .view-card-wrap {
        &:nth-child(1) {
          .board-list-card-wrap {
            @apply border-t-0;
          }
        }
      }
    }
  }
}
</style>
